import { TILE } from './constants';
import type { TileMap, Vec2 } from './types';

// Pure functions over a TileMap (derived from RunAndMove's tilemap.ts, without the class and the
// house/door/barricade machinery).

export const WALL = '#';
export const FLOOR = '.';

export function inBounds(map: TileMap, tx: number, ty: number): boolean {
  return tx >= 0 && ty >= 0 && tx < map.width && ty < map.height;
}

/** Solid tile? Everything outside the map is solid. */
export function isSolid(map: TileMap, tx: number, ty: number): boolean {
  return !inBounds(map, tx, ty) || map.tiles.charAt(ty * map.width + tx) === WALL;
}

/** The tile containing a world point. */
export function tileAt(p: Vec2): { x: number; y: number } {
  return { x: Math.floor(p.x / TILE), y: Math.floor(p.y / TILE) };
}

/** Centre of a tile in world units. */
export function tileCenter(tx: number, ty: number): Vec2 {
  return { x: (tx + 0.5) * TILE, y: (ty + 0.5) * TILE };
}

export function mapWidth(map: TileMap): number {
  return map.width * TILE;
}

export function mapHeight(map: TileMap): number {
  return map.height * TILE;
}

/** Largest displacement resolved in one go; longer moves are split so nothing tunnels through a wall. */
const MAX_SUBSTEP = TILE / 2;

/**
 * Moves a circle by (dx, dy) through the grid, sliding along solid tiles. Axis-separated so corners
 * do not snag; a circle that already overlaps a solid tile is pushed out towards where it came from.
 */
export function moveCircle(map: TileMap, x: number, y: number, r: number, dx: number, dy: number): Vec2 {
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / MAX_SUBSTEP));
  const sx = dx / steps;
  const sy = dy / steps;
  for (let i = 0; i < steps; i++) {
    const nx = slideAxis(map, x + sx, y, r, 'x', x);
    const ny = slideAxis(map, nx, y + sy, r, 'y', y);
    x = nx;
    y = ny;
  }
  return { x, y };
}

function slideAxis(map: TileMap, x: number, y: number, r: number, axis: 'x' | 'y', prev: number): number {
  const minTx = Math.floor((x - r) / TILE);
  const maxTx = Math.floor((x + r) / TILE);
  const minTy = Math.floor((y - r) / TILE);
  const maxTy = Math.floor((y + r) / TILE);
  for (let ty = minTy; ty <= maxTy; ty++) {
    for (let tx = minTx; tx <= maxTx; tx++) {
      if (!isSolid(map, tx, ty)) continue;
      // closest point on the tile AABB to the circle centre
      const cx = Math.max(tx * TILE, Math.min(x, (tx + 1) * TILE));
      const cy = Math.max(ty * TILE, Math.min(y, (ty + 1) * TILE));
      const ddx = x - cx;
      const ddy = y - cy;
      if (ddx * ddx + ddy * ddy >= r * r) continue;
      if (axis === 'x') {
        const moved = x - prev;
        if (moved > 0) x = tx * TILE - r - 0.01;
        else if (moved < 0) x = (tx + 1) * TILE + r + 0.01;
        else x = prev;
      } else {
        const moved = y - prev;
        if (moved > 0) y = ty * TILE - r - 0.01;
        else if (moved < 0) y = (ty + 1) * TILE + r + 0.01;
        else y = prev;
      }
    }
  }
  return axis === 'x' ? x : y;
}

/** Is a circle free of solid tiles here? */
export function circleFits(map: TileMap, x: number, y: number, r: number): boolean {
  const minTx = Math.floor((x - r) / TILE);
  const maxTx = Math.floor((x + r) / TILE);
  const minTy = Math.floor((y - r) / TILE);
  const maxTy = Math.floor((y + r) / TILE);
  for (let ty = minTy; ty <= maxTy; ty++) {
    for (let tx = minTx; tx <= maxTx; tx++) {
      if (!isSolid(map, tx, ty)) continue;
      const cx = Math.max(tx * TILE, Math.min(x, (tx + 1) * TILE));
      const cy = Math.max(ty * TILE, Math.min(y, (ty + 1) * TILE));
      if ((x - cx) ** 2 + (y - cy) ** 2 < r * r) return false;
    }
  }
  return true;
}

/**
 * Line of sight between two world points: DDA over the tiles on the segment; solid tiles block,
 * except the tiles containing the endpoints themselves.
 */
export function lineOfSight(map: TileMap, a: Vec2, b: Vec2): boolean {
  let tx = Math.floor(a.x / TILE);
  let ty = Math.floor(a.y / TILE);
  const ex = Math.floor(b.x / TILE);
  const ey = Math.floor(b.y / TILE);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const tDeltaX = stepX !== 0 ? Math.abs(TILE / dx) : Infinity;
  const tDeltaY = stepY !== 0 ? Math.abs(TILE / dy) : Infinity;
  let tMaxX = stepX !== 0 ? (stepX > 0 ? (tx + 1) * TILE - a.x : a.x - tx * TILE) / Math.abs(dx) : Infinity;
  let tMaxY = stepY !== 0 ? (stepY > 0 ? (ty + 1) * TILE - a.y : a.y - ty * TILE) / Math.abs(dy) : Infinity;
  let guard = map.width + map.height + 2;
  while ((tx !== ex || ty !== ey) && guard-- > 0) {
    if (tMaxX < tMaxY) {
      tx += stepX;
      tMaxX += tDeltaX;
    } else {
      ty += stepY;
      tMaxY += tDeltaY;
    }
    if (tx === ex && ty === ey) break;
    if (isSolid(map, tx, ty)) return false;
  }
  return true;
}

/**
 * Distance along `dir` (unit length) from `from` until the ray enters a solid tile, capped at
 * `maxDist`. 0 when `from` already lies in a wall. Used by the client for the aim line; the
 * simulation itself moves projectiles step by step and does not need it.
 */
export function castRay(map: TileMap, from: Vec2, dir: Vec2, maxDist: number): number {
  let tx = Math.floor(from.x / TILE);
  let ty = Math.floor(from.y / TILE);
  if (isSolid(map, tx, ty)) return 0;
  const stepX = dir.x > 0 ? 1 : dir.x < 0 ? -1 : 0;
  const stepY = dir.y > 0 ? 1 : dir.y < 0 ? -1 : 0;
  const tDeltaX = stepX !== 0 ? Math.abs(TILE / dir.x) : Infinity;
  const tDeltaY = stepY !== 0 ? Math.abs(TILE / dir.y) : Infinity;
  let tMaxX = stepX !== 0 ? (stepX > 0 ? (tx + 1) * TILE - from.x : from.x - tx * TILE) / Math.abs(dir.x) : Infinity;
  let tMaxY = stepY !== 0 ? (stepY > 0 ? (ty + 1) * TILE - from.y : from.y - ty * TILE) / Math.abs(dir.y) : Infinity;
  let guard = map.width + map.height + 2;
  while (guard-- > 0) {
    let t: number;
    if (tMaxX < tMaxY) {
      t = tMaxX;
      tx += stepX;
      tMaxX += tDeltaX;
    } else {
      t = tMaxY;
      ty += stepY;
      tMaxY += tDeltaY;
    }
    if (t >= maxDist) return maxDist;
    if (isSolid(map, tx, ty)) return t;
  }
  return maxDist;
}
